import {
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitTheme,
    AreaChart,
    Separator,
    Avatar,
    Button,
    Alert,
    Badge,
    Chart,
    Card,
    Chip,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    Bookmark,
    Search,
    Link2,
    Mail
} from "lucide-react-native";
import {
    Circle,
    Rect,
    Svg
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

const SearchIcon = createKitIcon(Search);

const LinkIcon = createKitIcon(Link2);

const MailIcon = createKitIcon(Mail);

const BookmarkIcon = createKitIcon(Bookmark);

const SECTIONS = [
    "s1",
    "s2",
    "s3",
    "s4",
    "s5"
];

const TAGS = [
    "history",
    "roasting",
    "origin",
    "filter"
];

const RELATED = [
    {
        key: "v60",
        circles: [
            "2",
            "5"
        ],
        area: "3"
    },
    {
        key: "origin",
        circles: [
            "1",
            "3"
        ],
        area: "1"
    },
    {
        key: "cezve",
        circles: [
            "4",
            "6"
        ],
        area: "4"
    }
];

const FOOTER_LINKS = [
    "rss",
    "about",
    "contact"
];

const CHART_ROWS = [
    {
        year: "1990",
        bags: 88
    },
    {
        year: "1995",
        bags: 96
    },
    {
        year: "2000",
        bags: 105
    },
    {
        year: "2005",
        bags: 116
    },
    {
        year: "2010",
        bags: 130
    },
    {
        year: "2015",
        bags: 148
    },
    {
        year: "2020",
        bags: 164
    },
    {
        year: "2025",
        bags: 177
    }
];

const stylesheet = StyleSheet.create({
    bar: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    footer: {
        justifyContent: "space-between",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    cover: {
        justifyContent: "flex-end",
        aspectRatio: 16 / 9,
        overflow: "hidden",
        width: "100%"
    },
    layout: {
        justifyContent: "center",
        flexDirection: "row",
        width: "100%"
    },
    coverArt: {
        position: "absolute",
        height: "100%",
        width: "100%",
        left: 0,
        top: 0
    },
    barLinks: {
        alignItems: "center",
        flexDirection: "row"
    },
    wrapRow: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        alignItems: "center",
        flexGrow: 1
    },
    grid: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    circle: {
        position: "absolute"
    },
    quote: {
        textAlign: "center"
    },
    relatedItem: {
        flexBasis: 240,
        flexShrink: 1,
        minWidth: 0,
        flexGrow: 1
    },
    shrink: {
        flexShrink: 1,
        minWidth: 0
    },
    article: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    authorBody: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    section: {
        width: "100%"
    },
    side: {
        flexGrow: 0
    }
});

const handlePress = () => undefined;

const Blog01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const scrollRef = useRef<ScrollView>(null);

    const [
        current,
        setCurrent
    ] = useState("s1");

    const [
        isFollowing,
        setIsFollowing
    ] = useState(false);

    const [
        isSaved,
        setIsSaved
    ] = useState(false);

    const [
        offsets,
        setOffsets
    ] = useState<Record<string, number>>({});

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const handleSectionLayout = (key: string) => (event: {
        nativeEvent: {
            layout: {
                y: number;
            };
        };
    }) => {
        const {
            y
        } = event.nativeEvent.layout;

        setOffsets((previous) => ({
            ...previous,
            [key]: y
        }));
    };

    const handleTocPress = (key: string) => () => {
        setCurrent(key);

        scrollRef.current?.scrollTo({
            y: offsets[key] ?? 0,
            animated: true
        });
    };

    const handleFollowToggle = () => setIsFollowing((previous) => !previous);

    const handleSaveToggle = () => setIsSaved((previous) => !previous);

    const renderHeading = (key: string) => <View
        onLayout={handleSectionLayout(key)}
        style={stylesheet.section}
        key={key}
    >
        <Text
            variant="headlineSmallSize"
            role="heading"
            color="high"
        >
            {t(`blocks.blog01.${key}.title`)}
        </Text>
    </View>;

    const renderParagraph = (key: string) => <Text
        variant="readingMediumSize"
        color="high"
        key={key}
    >
        {t(`blocks.blog01.${key}`)}
    </Text>;

    const renderCover = (item: typeof RELATED[number]) => <View
        style={[
            stylesheet.cover,
            {
                borderRadius: radiuses.surface,
                padding: spaces.spacingMd
            }
        ]}
    >
        <Svg
            preserveAspectRatio="xMidYMid slice"
            style={stylesheet.coverArt}
            viewBox="0 0 160 90"
        >
            <Rect
                fill={colors.chart.categoricalArea[item.area as "1"]}
                height={90}
                width={160}
            />
            <Circle
                fill={colors.chart.categorical[item.circles[0] as "1"]}
                cx={150}
                cy={5}
                r={42}
            />
            <Circle
                fill={colors.chart.categorical[item.circles[1] as "1"]}
                cx={106}
                cy={22}
                r={11}
            />
        </Svg>
        <Text
            variant="displaySmallSize"
            color="high"
        >
            {t(`blocks.blog01.related.${item.key}.cover`)}
        </Text>
    </View>;

    const renderRelated = (item: typeof RELATED[number]) => <View
        style={[
            stylesheet.relatedItem,
            {
                gap: spaces.spacingXs
            }
        ]}
        key={item.key}
    >
        {renderCover(item)}
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t(`blocks.blog01.related.${item.key}.meta`)}
        </Text>
        <Text
            variant="titleMediumSize"
            color="high"
        >
            {t(`blocks.blog01.related.${item.key}.title`)}
        </Text>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t(`blocks.blog01.related.${item.key}.text`)}
        </Text>
    </View>;

    const renderFooterLink = (key: string) => <Button
        title={t(`blocks.blog01.${key}`)}
        onPress={handlePress}
        variant="link"
        type="neutral"
        size="small"
        key={key}
    />;

    const renderTocItem = (key: string) => <Item
        onPress={handleTocPress(key)}
        isSelected={current === key}
        key={key}
    >
        <Item.Content>
            <Item.Title>
                {t(`blocks.blog01.${key}.toc`)}
            </Item.Title>
        </Item.Content>
    </Item>;

    return <ScrollView
        contentContainerStyle={stylesheet.page}
        ref={scrollRef}
    >
        <View
            style={[
                stylesheet.bar,
                stylesheet.section,
                {
                    paddingHorizontal: width < shapeConfigs.compactBreakpoint ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.barLinks,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {t("blocks.blog01.brand")}
                </Text>
                {
                    width >= shapeConfigs.compactBreakpoint && <>
                        <Button
                            title={t("blocks.blog01.topics")}
                            onPress={handlePress}
                            variant="tonal"
                            type="neutral"
                            size="small"
                        />
                        <Button
                            title={t("blocks.blog01.archive")}
                            onPress={handlePress}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                    </>
                }
            </View>
            <View
                style={[
                    stylesheet.barLinks,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    accessibilityLabel={t("blocks.blog01.search")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={SearchIcon}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.blog01.subscribe")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
        <Separator />
        <View
            style={[
                stylesheet.layout,
                {
                    paddingHorizontal: width < shapeConfigs.compactBreakpoint ? spaces.spacingMd : spaces.spacingLg,
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingVertical: spaces.spacingXl,
                    gap: spaces.spacingXxl
                }
            ]}
        >
            {
                isWide && <View
                    style={[
                        stylesheet.side,
                        {
                            width: shapeConfigs.sideListWidth,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Text
                        variant="labelMediumSize"
                        color="mid"
                    >
                        {t("blocks.blog01.tocTitle")}
                    </Text>
                    <View>
                        {SECTIONS.map(renderTocItem)}
                    </View>
                    <Separator />
                    <Text
                        variant="labelMediumSize"
                        color="mid"
                    >
                        {t("blocks.blog01.share")}
                    </Text>
                    <View
                        style={[
                            stylesheet.barLinks,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Button
                            accessibilityLabel={t("blocks.blog01.copyLink")}
                            onPress={handlePress}
                            variant="ghost"
                            icon={LinkIcon}
                            type="neutral"
                        />
                        <Button
                            accessibilityLabel={t("blocks.blog01.sendMail")}
                            onPress={handlePress}
                            icon={MailIcon}
                            variant="ghost"
                            type="neutral"
                        />
                        <Button
                            accessibilityLabel={t("blocks.blog01.save")}
                            variant={isSaved ? "tonal" : "ghost"}
                            onPress={handleSaveToggle}
                            icon={BookmarkIcon}
                            type="neutral"
                        />
                    </View>
                </View>
            }
            <View
                style={[
                    stylesheet.article,
                    {
                        maxWidth: shapeConfigs.readingMaxWidth,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={{
                        gap: spaces.spacingMd
                    }}
                >
                    <View
                        style={[
                            stylesheet.wrapRow,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Badge
                            title={t("blocks.blog01.category")}
                            variant="outline"
                            type="neutral"
                        />
                        <Text
                            variant="labelMediumSize"
                            color="mid"
                        >
                            {t("blocks.blog01.series")}
                        </Text>
                    </View>
                    <Text
                        variant="headlineMediumSize"
                        role="heading"
                        color="high"
                    >
                        {t("blocks.blog01.title")}
                    </Text>
                    <Text
                        variant="readingLargeSize"
                        color="mid"
                    >
                        {t("blocks.blog01.standfirst")}
                    </Text>
                    <View
                        style={[
                            stylesheet.wrapRow,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Avatar
                            title={t("blocks.blog01.author")}
                            size="small"
                        />
                        <View
                            style={stylesheet.shrink}
                        >
                            <Text
                                variant="labelLargeSize"
                                color="high"
                            >
                                {t("blocks.blog01.author")}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.blog01.dateRead")}
                            </Text>
                        </View>
                    </View>
                    {
                        !isWide && <View
                            style={[
                                stylesheet.barLinks,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Button
                                accessibilityLabel={t("blocks.blog01.copyLink")}
                                onPress={handlePress}
                                variant="ghost"
                                icon={LinkIcon}
                                type="neutral"
                            />
                            <Button
                                accessibilityLabel={t("blocks.blog01.sendMail")}
                                onPress={handlePress}
                                icon={MailIcon}
                                variant="ghost"
                                type="neutral"
                            />
                            <Button
                                accessibilityLabel={t("blocks.blog01.save")}
                                variant={isSaved ? "tonal" : "ghost"}
                                onPress={handleSaveToggle}
                                icon={BookmarkIcon}
                                type="neutral"
                            />
                        </View>
                    }
                </View>
                <Separator />
                {renderParagraph("intro")}
                {renderHeading("s1")}
                {renderParagraph("s1.p1")}
                {renderParagraph("s1.p2")}
                {renderHeading("s2")}
                {renderParagraph("s2.p1")}
                <Chart
                    config={{
                        bags: {
                            title: t("blocks.blog01.chartSeries"),
                            colorIndex: 1
                        }
                    }}
                    description={t("blocks.blog01.chartCaption")}
                    title={t("blocks.blog01.chartTitle")}
                    spreadBehaviour="stretch"
                    isAnimatedOnMount={true}
                >
                    <AreaChart
                        categoryTitle={t("blocks.blog01.chartYear")}
                        categoryKey="year"
                        data={CHART_ROWS}
                        plotSize="small"
                        curve="natural"
                    />
                </Chart>
                {renderHeading("s3")}
                {renderParagraph("s3.p1")}
                <View
                    style={{
                        paddingVertical: spaces.spacingLg,
                        gap: spaces.spacingMd
                    }}
                >
                    <Separator />
                    <Text
                        variant="readingLargeSize"
                        style={stylesheet.quote}
                        color="high"
                    >
                        {t("blocks.blog01.quote")}
                    </Text>
                    <Text
                        style={stylesheet.quote}
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.blog01.quoteBy")}
                    </Text>
                    <Separator />
                </View>
                {renderParagraph("s3.p2")}
                <Alert
                    description={t("blocks.blog01.note")}
                    title={t("blocks.blog01.noteTitle")}
                    type="info"
                />
                {renderHeading("s4")}
                {renderParagraph("s4.p1")}
                {renderHeading("s5")}
                {renderParagraph("s5.p1")}
                <View
                    style={[
                        stylesheet.wrapRow,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="labelMediumSize"
                        color="mid"
                    >
                        {t("blocks.blog01.tags")}
                    </Text>
                    {
                        TAGS.map((tag) => <Chip
                            title={t(`blocks.blog01.tag.${tag}`)}
                            onPress={handlePress}
                            variant="outline"
                            type="neutral"
                            key={tag}
                        />)
                    }
                </View>
                <Card
                    variant="filled"
                >
                    <Card.Content>
                        <View
                            style={[
                                stylesheet.wrapRow,
                                {
                                    alignItems: "flex-start",
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Avatar
                                title={t("blocks.blog01.author")}
                                size="large"
                            />
                            <View
                                style={[
                                    stylesheet.authorBody,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleSmallSize"
                                    color="high"
                                >
                                    {t("blocks.blog01.author")}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.blog01.authorRole")}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.blog01.bio")}
                                </Text>
                            </View>
                            <Button
                                title={t(isFollowing ? "blocks.blog01.following" : "blocks.blog01.follow")}
                                variant={isFollowing ? "tonal" : "outline"}
                                onPress={handleFollowToggle}
                                type="neutral"
                                size="small"
                            />
                        </View>
                    </Card.Content>
                </Card>
            </View>
        </View>
        <Separator />
        <View
            style={[
                stylesheet.section,
                {
                    paddingHorizontal: width < shapeConfigs.compactBreakpoint ? spaces.spacingMd : spaces.spacingLg,
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingVertical: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.bar,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="headlineSmallSize"
                    role="heading"
                    color="high"
                >
                    {t("blocks.blog01.relatedTitle")}
                </Text>
                <Button
                    title={t("blocks.blog01.all")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
            </View>
            <View
                style={[
                    stylesheet.grid,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {RELATED.map(renderRelated)}
            </View>
        </View>
        <Separator />
        <View
            style={[
                stylesheet.footer,
                stylesheet.section,
                {
                    paddingHorizontal: width < shapeConfigs.compactBreakpoint ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                style={stylesheet.shrink}
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.blog01.footer")}
            </Text>
            <View
                style={[
                    stylesheet.barLinks,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                {FOOTER_LINKS.map(renderFooterLink)}
            </View>
        </View>
    </ScrollView>;
};

export default Blog01;
